import { useState, useEffect } from 'react'
import moment from 'moment'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    Button,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    Slider,
} from '@duck/ui'
import { useTar, apis, TranslationKey } from '@/modules'
import { UserService } from '@duck/api-definitions'
import { useAtomValue, useSetAtom } from 'jotai'
import { setUserAtom, userAtom } from '@/store/userAtoms'
import { AxiosError } from 'axios'
import { toast } from 'sonner'
import { StandardLoader } from '../StandardLoader'
import { useUserSettings } from '@/hooks/useUserSettings'
import { isPartner } from '@/utils/roleGuard'
import { DiscoverySourcePicker, DiscoverySourceValue } from './DiscoverySourcePicker'

const professionOptions: { value: UserService.Profession; label: TranslationKey }[] = [
    { value: UserService.Profession.DataAnalyst, label: 'personalInfo.professionOptions.dataAnalyst' },
    { value: UserService.Profession.DataScientist, label: 'personalInfo.professionOptions.dataScientist' },
    { value: UserService.Profession.DataEngineer, label: 'personalInfo.professionOptions.dataEngineer' },
    { value: UserService.Profession.DataOperations, label: 'personalInfo.professionOptions.dataOperations' },
    { value: UserService.Profession.MachineLearningEngineer, label: 'personalInfo.professionOptions.machineLearningEngineer' },
    { value: UserService.Profession.SoftwareEngineer, label: 'personalInfo.professionOptions.softwareEngineer' },
    { value: UserService.Profession.RecruitmentManager, label: 'personalInfo.professionOptions.recruitmentManager' },
    { value: UserService.Profession.Other, label: 'personalInfo.professionOptions.other' },
]

export const PersonalInfoDialog = () => {
    const { t } = useTar()
    const user = useAtomValue(userAtom)
    const setUser = useSetAtom(setUserAtom)
    const { setSetting, getSetting } = useUserSettings()

    const [yearsOfXP, setYearsOfXP] = useState<number>(0)
    const [profession, setProfession] = useState<UserService.Profession | ''>('')
    const [discoverySource, setDiscoverySource] = useState<DiscoverySourceValue | null>(null)
    const [isSaving, setIsSaving] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [localDismissedPersonalInfo, setLocalDismissedPersonalInfo] = useState<boolean>(false)

    // Initialize form with existing personal info if available
    useEffect(() => {
        if (user?.personalInfo) {
            setYearsOfXP(user.personalInfo.yearsOfXP ?? 0)
            setProfession(user.personalInfo.profession ?? '')
            const { discoverySource: source, discoverySourceOther: other } = user.personalInfo
            setDiscoverySource(source ? { source, other } : null)
        }
    }, [user])

    const handleReject = async () => {
        setSetting('dismissedPersonalInfo', true)
        setLocalDismissedPersonalInfo(true)
    }

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        if (isSaving) return

        setError(null)

        // Validation
        if (discoverySource === null) {
            setError(t('personalInfo.errors.discoverySourceRequired'))
            return
        }

        if (yearsOfXP < 0) {
            setError(t('personalInfo.errors.yearsOfXPRequired'))
            return
        }

        if (!profession) {
            setError(t('personalInfo.errors.professionRequired'))
            return
        }

        try {
            setIsSaving(true)

            const updateRequest: UserService.UpdatePersonalInfoRequest = {
                yearsOfXP: yearsOfXP,
                profession: profession as UserService.Profession,
                discoverySource: discoverySource.source,
                discoverySourceOther: discoverySource.other,
            }

            const { data: updatedUser } = await apis.userService.updatePersonalInfoPut(updateRequest)

            // Update user atom with the new user data
            setUser(updatedUser)

            toast.success(t('personalInfo.saveSuccess'))
        } catch (err) {
            console.error('Failed to update personal info:', err)
            const errorMessage = err instanceof AxiosError
                ? err.response?.data?.error || t('personalInfo.saveError')
                : t('personalInfo.saveError')
            setError(errorMessage)
            toast.error(errorMessage)
            setSetting('dismissedPersonalInfo', true)
            setLocalDismissedPersonalInfo(true)
        } finally {
            setIsSaving(false)
        }
    }

    // When not to show:
    if (
        !user ||
        (user?.personId && moment(user.createdAt).isAfter(moment().subtract(1, 'day'))) ||
        getSetting('dismissedPersonalInfo') ||
        localDismissedPersonalInfo ||
        user.personalInfo?.profession ||
        isPartner(user) ||
        user.settings?.dismissedPersonalInfo
    ) {
        return null
    }

    return (
        <Dialog
            open
            onOpenChange={(open) => {
                // Prevent closing when saving or when personal info is missing
                if (!open && isSaving) {
                    return
                }
            }}
        >
            <DialogContent
                className="w-full h-full sm:w-auto sm:h-auto sm:max-h-[90vh] sm:max-w-md rounded-none sm:rounded-lg sm:min-w-[480px] [&>button[class*='absolute']]:hidden"
                onPointerDownOutside={(e) => isSaving && e.preventDefault()}
                onEscapeKeyDown={(e) => isSaving && e.preventDefault()}
                onInteractOutside={(e) => isSaving && e.preventDefault()}
            >
                <DialogHeader>
                    <DialogTitle className="text-lg sm:text-xl">
                        {t('personalInfo.title')}
                    </DialogTitle>
                    <DialogDescription>
                        {t('personalInfo.description')}
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Error Message */}
                    {error && (
                        <div className="bg-error/10 border border-error/20 rounded-lg p-4">
                            <div className="flex items-center">
                                <svg className="w-5 h-5 text-error me-2" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                </svg>
                                <p className="text-sm text-error">{error}</p>
                            </div>
                        </div>
                    )}

                    {/* Discovery Source */}
                    <div>
                        <label htmlFor="discoverySource" className="block text-sm font-medium text-foreground mb-2">
                            {t('personalInfo.discoverySource')}
                        </label>
                        <DiscoverySourcePicker
                            id="discoverySource"
                            value={discoverySource}
                            onChange={setDiscoverySource}
                            disabled={isSaving}
                            invalid={!!error && discoverySource === null}
                        />
                    </div>

                    {/* Years of XP */}
                    <div>
                        <label htmlFor="yearsOfXP" className="block text-sm font-medium text-foreground mb-2">
                            {t('personalInfo.yearsOfXPVALUE', { yearsOfXP: yearsOfXP === 10 ? '10+' : yearsOfXP })}
                        </label>
                        <div className="space-y-2">
                            <div className="flex items-center justify-between text-xs font-medium uppercase text-muted-foreground">
                                <span>0</span>
                                <span>10+</span>
                            </div>
                            <Slider
                                min={0}
                                max={10}
                                step={1}
                                value={[yearsOfXP]}
                                onValueChange={(newValue) => {
                                    const next = newValue[0] ?? 0
                                    const clampedValue = Math.min(Math.max(Math.round(next), 0), 10)
                                    setYearsOfXP(clampedValue)
                                }}
                                disabled={isSaving}
                                className="cursor-pointer"
                            />
                        </div>
                    </div>

                    {/* Profession */}
                    <div>
                        <label htmlFor="profession" className="block text-sm font-medium text-foreground mb-2">
                            {t('personalInfo.profession')}
                        </label>
                        <Select
                            value={profession}
                            onValueChange={(value) => setProfession(value as UserService.Profession)}
                            disabled={isSaving}
                        >
                            <SelectTrigger
                                className={`w-full ${error && !profession ? 'border-error' : ''}`}
                            >
                                <SelectValue placeholder={t('personalInfo.professionPlaceholder')} />
                            </SelectTrigger>
                            <SelectContent>
                                {professionOptions.map((option) => (
                                    <SelectItem key={option.value} value={option.value}>
                                        {t(option.label)}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-2">
                        <Button
                            type="button"
                            variant="ghost"
                            disabled={isSaving}
                            className="w-1/3"
                            onClick={handleReject}
                        >
                            {t('common.reject')}
                        </Button>
                        <Button
                            type="submit"
                            disabled={isSaving}
                            className="w-2/3"
                        >
                            {isSaving ? (
                                <>
                                    <StandardLoader size="sm" />
                                    <span className="ms-2">{t('common.saving')}</span>
                                </>
                            ) : (
                                t('common.save')
                            )}
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    )
}

